import React from 'react';

import { getInitials } from '@/utils/getInitials';
import { stringToColor } from '@/utils/stringToColor';

interface ExpertAvatarProps {
  firstName?: string | null;
  lastName?: string | null;
  name?: string;
  pictureUrl?: string | null;
  size?: number;
  variant?: 'person' | 'document';
}

const pxToRem = (px: number) => `${px / 10}rem`;

export function ExpertAvatar({
  firstName,
  lastName,
  name,
  pictureUrl,
  size = 36,
  variant = 'person',
}: ExpertAvatarProps): React.ReactNode {
  const dim = pxToRem(size);
  const fontSize = pxToRem(size * 0.36);

  if (pictureUrl) {
    return (
      <img
        src={pictureUrl}
        alt={name ?? getInitials(firstName, lastName)}
        className="shrink-0 rounded-full object-cover"
        style={{ width: dim, height: dim }}
      />
    );
  }

  if (variant === 'document') {
    return (
      <div
        className="flex shrink-0 items-center justify-center rounded-full bg-on-surface-subtle"
        style={{ width: dim, height: dim }}
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ width: pxToRem(size * 0.5), height: pxToRem(size * 0.5) }}
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      </div>
    );
  }

  const initials = getInitials(firstName, lastName);
  const bgColor = stringToColor(name ?? firstName ?? '', 700);

  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{ width: dim, height: dim, fontSize, backgroundColor: bgColor }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}
